/**
 * ATL-1452 — WHICH BUNDLE IS RUNNING, and is it below the floor?
 *
 * The existing lockout (`versionLockout.js`) compares `config/app.minVersion` against the MARKETING
 * version, which moves only on a native store build — every OTA reports the same value (ATL-877), so
 * it can say "native build ≥ X" and never "OTA bundle ≥ v7.1.100". This module identifies the running
 * bundle the way the read-cutover gate does (`ota-coverage.util.ts`), so the app and the gate agree by
 * construction about which bundle is current:
 *
 *   • native OTA launch — the instant decoded from the EAS update id (a UUIDv7);
 *   • native EMBEDDED launch — UNKNOWN, deliberately. Its id is a UUIDv4 with no instant, and its
 *     `createdAt` is the binary's BUILD time, which the gate does not use (it dates binaries by the
 *     commit that declared their version, `STORE_BINARY_RELEASES`) — a binary built after the floor
 *     from an older commit would pass here while lacking the fix. An embedded launch fetches the
 *     current OTA on launch anyway (expo-updates `checkAutomatically: ON_LOAD`), so leaving it
 *     unblocked costs one session, not a stranded till;
 *   • web — the build stamp `EXPO_PUBLIC_WEB_BUILD_AT` baked into the bundle at export (ATL-895).
 *
 * THE FAIL-OPEN RULES, which are the point of `evaluateBundleFloor`: an absent, unparseable or
 * unknown-typed floor, and an identity that cannot be read, NEVER block. A lockout that bricks every
 * till over a config typo is a worse outage than the one it guards against — the same direction
 * `compareVersions` already takes for `minVersion`.
 *
 * ⚠️ This code only runs in bundles that CONTAIN it. It protects every FUTURE floor raise and makes an
 * update immediate instead of next-launch; it cannot reach a bundle older than the release that ships
 * it (ATL-1452 comment, 2026-09-26).
 */

export type BundleIdentitySource = 'update-id' | 'web-build-at' | 'unknown'

export interface BundleIdentity {
  /** The instant the running bundle was published/built, or null when unknowable. */
  at: Date | null
  source: BundleIdentitySource
}

/** The instant carried by a UUIDv7 (its first 48 bits, ms since epoch). Null for anything else. */
export const decodeUuidV7Instant = (id: string | null | undefined): Date | null => {
  if (typeof id !== 'string') return null
  const m = /^([0-9a-f]{8})-([0-9a-f]{4})-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.exec(id)
  if (!m) return null
  const ms = parseInt(m[1] + m[2], 16)
  return Number.isFinite(ms) && ms > 0 ? new Date(ms) : null
}

export interface RunningBundleInputs {
  platform: string
  updateId?: string | null
  isEmbeddedLaunch?: boolean | null
  webBuildAt?: string | null
}

export const resolveRunningBundleInstant = ({
  platform,
  updateId,
  isEmbeddedLaunch,
  webBuildAt,
}: RunningBundleInputs): BundleIdentity => {
  if (platform === 'web') {
    // Parsed EXACTLY as the gate parses it (parseGateInstant): a zone-less stamp is UTC, never local.
    const ms = typeof webBuildAt === 'string' && webBuildAt ? parseGateInstant(webBuildAt) : NaN
    const at = Number.isNaN(ms) ? null : new Date(ms)
    return at ? { at, source: 'web-build-at' } : { at: null, source: 'unknown' }
  }
  if (isEmbeddedLaunch === true) return { at: null, source: 'unknown' }
  // Only a UUIDv7 id is dated — exactly what the gate's `decodeUpdatePublishedAt` accepts. An OTA
  // launch reporting anything else (a UUIDv4, ATL-902) is `unverifiable` to the gate and UNKNOWN
  // here, so the two can never give the same bundle different verdicts.
  const decoded = decodeUuidV7Instant(updateId)
  return decoded ? { at: decoded, source: 'update-id' } : { at: null, source: 'unknown' }
}

export type BundleFloorOutcome =
  | 'ok'
  | 'below-soft'
  | 'below-hard'
  | 'no-floor'
  | 'invalid-floor'
  | 'other-runtime'
  | 'unknown-identity'

export interface BundleFloorEvaluation {
  outcome: BundleFloorOutcome
  floor: string | null
  runningAt: string | null
  source: BundleIdentitySource
}

/** The `config/app` fields this lockout reads. Every one may be absent or malformed. */
export interface BundleFloorConfig {
  /** Native floor: the EAS OTA publish instant (UUIDv7-decoded, as the gate dates bundles). */
  minBundlePublishedAt?: unknown
  /**
   * WEB floor, separate on purpose: a web bundle is dated by its EXPORT stamp, which lands minutes
   * BEFORE the same release's OTA instant, and a web till has no OTA and no store to fall back on.
   * One shared floor would lock every browser out of the very release it names. Absent ⇒ web is
   * never blocked.
   */
  minWebBundleBuiltAt?: unknown
  /**
   * The runtimeVersion the native floor applies to — REQUIRED for a native floor. After a runtime
   * bump the old runtime stops receiving OTAs, so a floor named in the new runtime's instants would
   * otherwise demand a store install from every device on the old one.
   */
  bundleFloorRuntimeVersion?: unknown
  /** 'hard' | 'soft' for the NATIVE floor. */
  bundleLockoutType?: unknown
  /**
   * 'hard' | 'soft' for the WEB floor — its own, so the web floor (a different instant, and no OTA
   * or store to fall back on) can be observed in `soft` while native is already `hard`. Absent ⇒ the
   * web floor is invalid and never blocks.
   */
  webBundleLockoutType?: unknown
}

/**
 * The floor fields of `config/app` as the gate PERSISTS them (the last server copy, for the next
 * launch to open early on — see useBundleGate): JSON-safe, a Firestore Timestamp floor stored as its
 * canonical ISO instant, anything non-primitive dropped (it evaluates as invalid either way).
 */
export const persistableFloorConfig = (data: BundleFloorConfig | null): BundleFloorConfig => {
  const primitive = (v: unknown): unknown =>
    typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean' ? v : null
  const floor = (v: unknown): unknown => (isTimestampLike(v) ? normaliseFloor(v).floor : primitive(v))
  return {
    minBundlePublishedAt: floor(data?.minBundlePublishedAt),
    minWebBundleBuiltAt: floor(data?.minWebBundleBuiltAt),
    bundleFloorRuntimeVersion: primitive(data?.bundleFloorRuntimeVersion),
    bundleLockoutType: primitive(data?.bundleLockoutType),
    webBundleLockoutType: primitive(data?.webBundleLockoutType),
  }
}

/**
 * The floor instant, parsed EXACTLY as the gate parses instants (`parseInstant`, ota-coverage.util):
 * a `Z` / `±HH:MM` / `±HHMM` zone is honoured, and a zone-less stamp is UTC — never the device's
 * local time, which would move the floor by hours from till to till. `±HHMM` is rewritten to
 * `±HH:MM` first, the form every engine (Hermes included) parses.
 */
const parseZonedInstant = (raw: string): number => {
  // What operators actually paste, normalised to the one form every engine (Hermes included) parses:
  // a space separator, a lowercase 'z', and PostHog's microsecond precision ('…19.181000Z') are all
  // real — Hermes returns NaN for some of them, which would make a hard floor silently invalid.
  const v = raw
    .trim()
    .replace(/^(\d{4}-\d{2}-\d{2}) /, '$1T')
    .replace(/z$/, 'Z')
    .replace(/(\.\d{3})\d+/, '$1')
  const withColon = v.replace(/([+-]\d{2})(\d{2})$/, '$1:$2')
  const ms = Date.parse(/(Z|[+-]\d{2}:\d{2})$/.test(withColon) ? withColon : `${withColon}Z`)
  return Number.isNaN(ms) ? NaN : ms
}

/**
 * The GATE's instant parser, byte for byte (`parseInstant`, ota-coverage.util — differential-tested
 * against it): used for the web build STAMP, which the gate also parses, so the app and the gate can
 * never read the same stamp differently. (Floors are operator input and use the more forgiving
 * parseZonedInstant above; the gate never parses a floor from config.)
 */
export const parseGateInstant = (value: string): number => {
  const ms = Date.parse(/(Z|[+-]\d{2}:?\d{2})$/.test(value) ? value : `${value}Z`)
  return Number.isNaN(ms) ? NaN : ms
}

/** A Firestore `Timestamp` as the SDK hands it back (the console's "timestamp" field type). */
const isTimestampLike = (v: unknown): v is { toDate: () => Date } =>
  typeof v === 'object' && v !== null && typeof (v as { toDate?: unknown }).toDate === 'function'

/**
 * The floor as an instant, from either form an operator can store:
 *   • a string — a zone-less '2026-09-23T16:35:19' is read as UTC (parseZonedInstant), never in the
 *     DEVICE's local time, which would move the floor by hours from till to till and away from the
 *     gate's UTC instant;
 *   • a Firestore Timestamp — the console's natural type for a date. It is an absolute instant, so it
 *     is accepted (and normalised to its ISO string, the form telemetry and the release compare).
 * Anything else is `invalid` — fail open, never a guess.
 */
const normaliseFloor = (raw: unknown): { floor: string | null; ms: number } => {
  if (typeof raw === 'string') return { floor: raw, ms: parseZonedInstant(raw) }
  if (isTimestampLike(raw)) {
    try {
      const d = raw.toDate()
      const ms = d instanceof Date ? d.getTime() : NaN
      return Number.isNaN(ms) ? { floor: null, ms: NaN } : { floor: d.toISOString(), ms }
    } catch {
      return { floor: null, ms: NaN }
    }
  }
  return { floor: null, ms: NaN }
}

export const evaluateBundleFloor = ({
  identity,
  platform,
  runtimeVersion,
  config,
}: {
  identity: BundleIdentity
  platform: string
  runtimeVersion?: string | null
  config: BundleFloorConfig | null | undefined
}): BundleFloorEvaluation => {
  const raw = platform === 'web' ? config?.minWebBundleBuiltAt : config?.minBundlePublishedAt
  const normalised = normaliseFloor(raw)
  const base = {
    // The CANONICAL instant (ISO), not the raw string: equivalent spellings ('…19Z', '…19.000Z',
    // '…19z', a trailing space) are ONE floor — for the release, the back-off and a re-point alike.
    floor: Number.isNaN(normalised.ms) ? normalised.floor : new Date(normalised.ms).toISOString(),
    runningAt: identity.at ? identity.at.toISOString() : null,
    source: identity.source,
  }
  if (raw === undefined || raw === null || raw === '') return { outcome: 'no-floor', ...base }
  const floor = normalised.ms
  const type = platform === 'web' ? config?.webBundleLockoutType : config?.bundleLockoutType
  if (Number.isNaN(floor) || (type !== 'hard' && type !== 'soft')) {
    return { outcome: 'invalid-floor', ...base }
  }
  if (platform !== 'web') {
    const scope = config?.bundleFloorRuntimeVersion
    // The console stores `5.0` as a NUMBER by default; that is the same runtime as the string '5.0',
    // so it is accepted rather than rejected (which would silently make `hard` enforce nothing).
    const scopeOk =
      (typeof scope === 'string' && scope !== '') ||
      (typeof scope === 'number' && Number.isFinite(scope))
    if (!scopeOk) return { outcome: 'invalid-floor', ...base }
    // A number is matched only against the decimal STRINGS that denote it exactly ('5' / '5.0' for
    // 5, '5.1' for 5.1, '5.25' for 5.25) — never by Number(runtime) ('5.10' would equal 5.1) and
    // never by rounding (5.25 must not match '5.3').
    const sameRuntime =
      typeof scope === 'string'
        ? runtimeVersion === scope
        : runtimeVersion === String(scope) ||
          (Number.isInteger(scope) && runtimeVersion === `${scope}.0`)
    if (!sameRuntime) return { outcome: 'other-runtime', ...base }
  }
  if (!identity.at) return { outcome: 'unknown-identity', ...base }
  if (identity.at.getTime() >= floor) return { outcome: 'ok', ...base }
  return { outcome: type === 'hard' ? 'below-hard' : 'below-soft', ...base }
}

/**
 * Should the till be bundle-locked after this check? (The gate's stream is server snapshots only.)
 *
 *   • It is newly locked ONLY at the launch — the process's first server snapshot — on `below-hard`;
 *     never later (the cashier may be mid-sale, and the lockout restarts the app). So a floor the
 *     lock's attempt already released cannot be re-taken in this process: the next LAUNCH decides.
 *   • Once locked it is RELEASED by any later snapshot that is not `below-hard` — removing the floor
 *     or setting it to soft is the kill switch, and it must work without killing the app (ATL-888).
 *   (Only SERVER snapshots reach this: a cached one never locks or releases — see useBundleGate.)
 */
export const nextBundleLock = ({
  locked,
  outcome,
  trigger,
}: {
  locked: boolean
  outcome: BundleFloorOutcome
  trigger: 'launch' | 'foreground'
}): boolean => {
  if (locked) return outcome === 'below-hard'
  return outcome === 'below-hard' && trigger === 'launch'
}

/**
 * After an update attempt for a floor ended in a way that the NEXT launch would only repeat, later
 * LAUNCHES do not lock for that floor for this long:
 *   • 'no-newer-update' — the channel has nothing newer (a floor above the newest OTA: a typo, a
 *     failed publish); every launch would lock, check and release again;
 *   • 'restarted' — a restart into the update was issued; if the relaunch is STILL below the floor
 *     (expo-updates rolled the update back, or reloaded another one), restarting again would loop;
 *   • 'update-failed' — every try to check/download failed (a network that reaches Firestore but
 *     not the EAS CDN); every launch would otherwise be held for the whole attempt, forever.
 * One hour, not more: right after a publish the EAS manifest/CDN may simply not have propagated.
 */
export const UPDATE_BACKOFF_MS = 60 * 60 * 1000

export type UpdateBackoffReason = 'no-newer-update' | 'restarted' | 'update-failed'

/** The one persisted fact (one key, overwritten): `floor` was backed off at `at` (ms) for `reason`. */
export interface UpdateBackoffRecord {
  floor: string
  at: number
  reason: UpdateBackoffReason
}

/** A record dated this far in the FUTURE means the clock moved backwards since: it is ignored. */
const CLOCK_SKEW_TOLERANCE_MS = 60 * 1000

/**
 * The reason `record` backs off `floor` at `now`, or null. Another floor, garbage, an unknown reason,
 * a stale record or one dated in the future ⇒ null (fail open to trying the update).
 */
export const updateBackoffActive = (
  record: unknown,
  floor: string,
  now: number
): UpdateBackoffReason | null => {
  if (typeof record !== 'object' || record === null) return null
  const r = record as Partial<UpdateBackoffRecord>
  if (r.floor !== floor || typeof r.at !== 'number' || !Number.isFinite(r.at)) return null
  if (r.reason !== 'no-newer-update' && r.reason !== 'restarted' && r.reason !== 'update-failed') {
    return null
  }
  if (r.at > now + CLOCK_SKEW_TOLERANCE_MS) return null
  return now - r.at < UPDATE_BACKOFF_MS ? r.reason : null
}

/**
 * Per-PROCESS gate state. The gate (useBundleGate) is mounted ONCE, in Routes, above the signed-in /
 * signed-out branch — so "launch" means the process start, not a sign-in, and a sign-out/sign-in never
 * remounts it. This record only guards a remount of that root itself (Fast Refresh, an error
 * boundary): a second mount never newly locks, and inherits the lock as it stands.
 */
export const bundleGateProcess = {
  launched: false,
  locked: false,
  /** The floor the lock holds for — the one the attempt names back when it ends. */
  lockedFloor: null as string | null,
  /** Signature of the last `bundle_gate_evaluated` sent (see shouldReportBundleEvaluation). */
  lastReportedSignature: null as string | null,
  /** Whether the lockout screen has been SEEN in this process (see markBundleLockoutShown). */
  lockReported: false,
  /**
   * A restart has been ISSUED (reloadAsync / location.reload called) and has not failed. From then on
   * nothing releases the lock — not the server, not a timer: an issued restart cannot be called back,
   * and handing the till back under it would restart it mid-sale. Cleared only by a restart that FAILS.
   */
  restartIssued: false,
}

export const resetBundleGateProcessForTests = (): void => {
  Object.assign(bundleGateProcess, {
    launched: false,
    locked: false,
    lockedFloor: null,
    lastReportedSignature: null,
    lockReported: false,
    restartIssued: false,
  })
}

/** The bundle lockout screen was SEEN (mounted): true the first time in this process. */
export const markBundleLockoutShown = (): boolean => {
  if (bundleGateProcess.lockReported) return false
  bundleGateProcess.lockReported = true
  return true
}

/**
 * Report an evaluation whenever what it SAYS changes — not once per process. POS tills run for days:
 * a till that reported `no-floor` on Monday must report `below-soft` after a soft floor is set on
 * Tuesday, or the observe-only rollout's count of who WOULD be blocked excludes most of the fleet.
 */
export const shouldReportBundleEvaluation = (
  evaluation: Pick<BundleFloorEvaluation, 'outcome' | 'floor'>,
  locked: boolean
): boolean => {
  const signature = `${evaluation.outcome}|${evaluation.floor ?? ''}|${locked}`
  if (signature === bundleGateProcess.lastReportedSignature) return false
  bundleGateProcess.lastReportedSignature = signature
  return true
}
